import { LatLng, LatLngBounds, LATLNG_BUDAPEST } from './map';
import { SortableOpenCloseTime } from './parcel-shop.utils';

// parcel shops

export type ParcelShopId = string;

export interface ParcelShopFromAPI {
	id: ParcelShopId;
	name: string;
	description: string;
	contact: ParcelShopContactInfo;
	location: LatLng;
	hours: ParcelShopHours[];
	pickupTime: string;
	holiday: { start: string; end: string } | null;
	features: {
		isCodHandler: boolean;
		isParcelLocker: boolean;
		acceptsCash: boolean;
		acceptsCard: boolean;
		dropoffpoint: boolean;
	};
}

export interface ParcelShop {
	id: ParcelShopId;
	name: string;
	description: string;
	contact: ParcelShopContactInfo;
	location: LatLng;
	hours: ParcelShopHours[];
	pickupTime: string;
	holiday: { start: string; end: string } | null;
	features: ParcelShopFeatures;
	distance?: number;
	isOpenNow?: ParcelShopIsOpen;
	_sortedHours: SortableOpenCloseTime[];
}

export interface ParcelShopWithCalculatedDetails extends ParcelShop {
	isOpenNow: ParcelShopIsOpen;
}

export type ParcelShopHours = [day: 1 | 2 | 3 | 4 | 5 | 6 | 7, open: string, close: string, break_start?: string, break_end?: string];

export type ParcelShopContactInfo = {
	countryCode: string;
	zipCode: string;
	city: string;
	address: string;
	phone: string;
	email: string;
	vendorUrl: string;
};

// static features

export const FEATURE_COD = 'cash-on-delivery';
export const FEATURE_DROPOFFPOINT = 'dropoffpoint';
export const FEATURE_ACCEPTS_CASH_PAYMENTS = 'accepts-cash-payments';
export const FEATURE_ACCEPTS_CARD_PAYMENTS = 'accepts-card-payments';
export const FEATURE_GLS_PARCEL_LOCKER = 'gls-parcel-locker';
export const FEATURE_OPEN_AFTER_8PM = 'open-after-8pm';
export const FEATURE_OPEN_ON_WEEKENDS = 'open-on-weekends';

export type ParcelShopStaticFeature =
	| typeof FEATURE_COD
	| typeof FEATURE_DROPOFFPOINT
	| typeof FEATURE_ACCEPTS_CASH_PAYMENTS
	| typeof FEATURE_ACCEPTS_CARD_PAYMENTS
	| typeof FEATURE_GLS_PARCEL_LOCKER
	| typeof FEATURE_OPEN_AFTER_8PM
	| typeof FEATURE_OPEN_ON_WEEKENDS;
export type ParcelShopStaticFeatures = {
	[K in ParcelShopStaticFeature]: boolean;
};
export const parcelShopStaticFeaturesDefaults: ParcelShopStaticFeatures = {
	[FEATURE_COD]: false,
	[FEATURE_DROPOFFPOINT]: false,
	[FEATURE_ACCEPTS_CASH_PAYMENTS]: false,
	[FEATURE_ACCEPTS_CARD_PAYMENTS]: false,
	[FEATURE_GLS_PARCEL_LOCKER]: false,
	[FEATURE_OPEN_AFTER_8PM]: false,
	[FEATURE_OPEN_ON_WEEKENDS]: false,
};

// dynamic features

export const FEATURE_OPEN = 'open';

export type ParcelShopDynamicFeature = typeof FEATURE_OPEN;
export type ParcelShopDynamicFeatures = {
	[K in ParcelShopDynamicFeature]: boolean;
};
export const parcelShopDynamicFeaturesDefaults: ParcelShopDynamicFeatures = {
	[FEATURE_OPEN]: true,
};
// features combined

export type ParcelShopFeature = ParcelShopStaticFeature | ParcelShopDynamicFeature;
export type ParcelShopFeatures = ParcelShopStaticFeatures & ParcelShopDynamicFeatures;
export const parcelShopFeaturesDefaults: ParcelShopFeatures = {
	...parcelShopStaticFeaturesDefaults,
	...parcelShopDynamicFeaturesDefaults,
};

export type ParcelShopSearchParams = {
	query: string | null;
	features: ParcelShopFeatures;
};

export type ParcelShopFilter = {
	location: LatLng | null;
	bounds: LatLngBounds | null;
	features: ParcelShopFeatures;
};

export const initialParcelShopFilter: ParcelShopFilter = {
	location: LATLNG_BUDAPEST,
	bounds: null,
	features: {
		...parcelShopFeaturesDefaults,
		[FEATURE_OPEN]: false,
	},
};

export type ParcelShopDistances = {
	[K: string]: number;
};

export type ParcelShopIsOpen = {
	isOpen: boolean;
	willOpenAt?: [day: number, hour: string];
	willCloseAt?: [day: number, hour: string];
};
